import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        {/* Main Navigation with Contact Info */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/zmxuftfb_GFU_logo-removebg-preview.png" 
                alt="Green Frog Utilities" 
                className="h-12 md:h-14"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-4">
              <Link 
                to="/" 
                className={`text-gray-700 hover:text-emerald-600 font-medium transition-colors ${
                  isActive('/') ? 'text-emerald-600' : ''
                }`}
              >
                Home
              </Link>
              
              <div 
                className="relative group"
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                  Our Services
                  <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-lg py-2 border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link 
                    to="/services/design" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Design
                  </Link>
                  <Link 
                    to="/services/connections" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Gas Connections
                  </Link>
                  <Link 
                    to="/water-connections" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Water Connections
                  </Link>
                  <Link 
                    to="/electrical-connections" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Electrical Connections
                  </Link>
                  <Link 
                    to="/services/construction" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Construction
                  </Link>
                  <Link 
                    to="/metering" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Metering
                  </Link>
                </div>
              </div>

              <div 
                className="relative group"
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                  Company
                  <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-lg py-2 border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link 
                    to="/about" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    About Us
                  </Link>
                  <Link 
                    to="/accreditations" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Accreditations
                  </Link>
                  <Link 
                    to="/why-choose-green-frog" 
                    className="block px-4 py-2 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                  >
                    Why Choose Us
                  </Link>
                </div>
              </div>

              <Link 
                to="/faq" 
                className={`text-gray-700 hover:text-emerald-600 font-medium transition-colors ${
                  isActive('/faq') ? 'text-emerald-600' : ''
                }`}
              >
                FAQ
              </Link>
              
              <div className="flex items-center gap-4 border-l border-gray-200 pl-6 ml-2">
                <a href="tel:+441675488644" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors text-sm">
                  <Phone className="h-4 w-4" />
                  <span className="font-medium">01675 488644</span>
                </a>
                <a href="mailto:contact@greenfrogutilities.co.uk" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors text-sm">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">Email Us</span>
                </a>
              </div>
              
              <Link to="/contact">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get a Quote
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden pb-4 space-y-2">
              <Link 
                to="/" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/services/design" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Design
              </Link>
              <Link 
                to="/services/connections" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Gas Connections
              </Link>
              <Link 
                to="/water-connections" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Water Connections
              </Link>
              <Link 
                to="/electrical-connections" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Electrical Connections
              </Link>
              <Link 
                to="/services/construction" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Construction
              </Link>
              <Link 
                to="/metering" 
                className="block py-2 pl-4 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Metering
              </Link>
              <Link 
                to="/about" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>
              <Link 
                to="/accreditations" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Accreditations
              </Link>
              <Link 
                to="/why-choose-green-frog" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Why Choose Us
              </Link>
              <Link 
                to="/faq" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link 
                to="/contact" 
                className="block py-2 text-gray-700 hover:text-emerald-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get a Quote
              </Link>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="bg-white p-3 rounded inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/zmxuftfb_GFU_logo-removebg-preview.png" 
                  alt="Green Frog Utilities" 
                  className="h-12"
                />
              </div>
              <p className="text-gray-400 text-sm">
                Fully managed utilities solutions. Take the stress out of getting connected with our end-to-end, co-ordinated and integrated multi-utility solutions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-emerald-400 transition-colors">Home</Link></li>
                <li><Link to="/services/design" className="hover:text-emerald-400 transition-colors">Design Services</Link></li>
                <li><Link to="/services/connections" className="hover:text-emerald-400 transition-colors">Gas Connections</Link></li>
                <li><Link to="/water-connections" className="hover:text-emerald-400 transition-colors">Water Connections</Link></li>
                <li><Link to="/electrical-connections" className="hover:text-emerald-400 transition-colors">Electrical Connections</Link></li>
                <li><Link to="/services/construction" className="hover:text-emerald-400 transition-colors">Construction</Link></li>
                <li><Link to="/metering" className="hover:text-emerald-400 transition-colors">Metering</Link></li>
                <li><Link to="/accreditations" className="hover:text-emerald-400 transition-colors">Accreditations</Link></li>
                <li><Link to="/why-choose-green-frog" className="hover:text-emerald-400 transition-colors">Why Choose Us</Link></li>
                <li><Link to="/green-frog-group" className="hover:text-emerald-400 transition-colors">Utilities and Asset Management</Link></li>
                <li><Link to="/faq" className="hover:text-emerald-400 transition-colors">FAQ</Link></li>
              </ul>
              <h3 className="text-lg font-semibold mb-4 mt-6">Policies</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/wdhpaklj_Data%20Protection%20Policy%20-%20HR.POL.20b%20-%20Issue%202.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Data Protection Policy</a></li>
                <li><a href="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/198vcfkq_Enquiry%20Privacy%20Notice%20-%20HR.POL.20g%20-%20Issue%201.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Enquiry Privacy Notice</a></li>
                <li><a href="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/4poilfck_Cookie%20Policy%20-%20HR.POL.20c%20-%20Issue%201%20%281%29.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Cookie Policy</a></li>
                <li><a href="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/l7nl1ald_Anti-Bribery%20%26%20Anti-Corruption%20Policy%20-%20HR.POL.36%20Issue%203.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Anti-Bribery & Anti-Corruption Policy</a></li>
                <li><a href="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/k7bqxgdl_Anti-Money%20Laundering%20Policy%20-%20HR.POL.37%20-%20Issue%201.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Anti-Money Laundering Policy</a></li>
                <li><a href="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/267lszyj_Anti-slavery%20Policy%20-%20HR.POL.38%20-%20Issue%201.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Anti-Slavery Policy</a></li>
                <li><a href="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/tjzjpflx_GFU-SOP-002%20-%20Complaints%20Procedure.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Complaints</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white mb-1">Office</p>
                    <span>Unit 4 Ninian Park, Ninian Way, Tamworth, Staffordshire B77 5ES</span>
                  </div>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-emerald-400" />
                  <a href="tel:+441675488644" className="hover:text-emerald-400 transition-colors">01675 488644</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-400" />
                  <a href="mailto:contact@greenfrogutilities.co.uk" className="hover:text-emerald-400 transition-colors">contact@greenfrogutilities.co.uk</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Green Frog Utilities. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
